import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { e as emblaNavigation } from './navigation-DGcbxkcb.js';
import { e as emblaScrollbar } from './scrollbar-CKxfKmNg.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';

//#region src/function/noop.ts
/**
* A no-operation function that does nothing.
* This can be used as a placeholder or default function.
*
* @example
* noop(); // Does nothing
*
* @returns This function does not return anything.
*/
function noop() {}

/**
 * @typedef {object} IntersectOptions
 * @property {Element|Document|null} [root] - The intersection root. Null = browser viewport.
 * @property {string} [rootMargin] - Margin around the root, e.g. '100% 0px' to fire a viewport early.
 * @property {number|number[]} [threshold] - Visible ratio needed to fire (0 = any pixel).
 * @property {boolean} [once] - Disconnect after the first intersection. Defaults to true.
 */

/**
 * Run a callback when an element intersects the viewport, mirroring onClickOutside:
 * pass an element and callback, get back an unsubscribe function.
 * @param {Element} element - The element to observe.
 * @param {(entry: IntersectionObserverEntry) => void} callback - Called on intersection.
 * @param {IntersectOptions} [options] - Observer configuration.
 * @returns {() => void} A function that stops observing when called.
 */
function onIntersect(element, callback, options = {}) {
  if (!element) return noop;

  const { root = null, rootMargin = '0px', threshold = 0, once = true } = options;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        callback(entry);
        if (once) observer.disconnect();
      });
    },
    { root, rootMargin, threshold }
  );

  observer.observe(element);

  return () => observer.disconnect();
}

const defaultOptions = {
  active: true,
  breakpoints: {},
  announceChanges: false,
  carouselAriaLabel: 'Carousel',
  carouselAriaRoleDescription: 'carousel',
  carouselRole: 'region',
  previousButtonAriaLabel: 'Go to previous Slide',
  nextButtonAriaLabel: 'Go to next Slide',
  slideAriaRoleDescription: 'slide',
  slideRole: 'group',
  rootNode: null,
  slideAriaLabel: (hasAnyGroupedSlides, firstSlideIndex, lastSlideIndex, totalSlides, selectedSnapIndex, totalSnaps) => {
    const slide = firstSlideIndex + 1;
    const lastSlide = lastSlideIndex + 1;
    const isGrouped = firstSlideIndex !== lastSlideIndex;
    const groupInfo = `(group ${selectedSnapIndex + 1} of ${totalSnaps})`;
    if (!hasAnyGroupedSlides) {
      return `Slide ${slide} of ${totalSlides}`;
    }
    if (!isGrouped) {
      return `Slide ${slide} of ${totalSlides} ${groupInfo}`;
    }
    return `Slides ${slide}-${lastSlide} of ${totalSlides} ${groupInfo}`;
  },
  dotButtonAriaLabel: (hasAnyGroupedSlides, firstSlideIndex, lastSlideIndex, totalSlides, selectedSnapIndex, totalSnaps) => {
    const slide = firstSlideIndex + 1;
    const lastSlide = lastSlideIndex + 1;
    const isSlideGrouped = firstSlideIndex !== lastSlideIndex;
    const groupInfo = `(group ${selectedSnapIndex + 1} of ${totalSnaps})`;
    if (!hasAnyGroupedSlides) {
      return `Go to slide ${slide} of ${totalSlides}`;
    }
    if (!isSlideGrouped) {
      return `Go to slide ${slide} of ${totalSlides} ${groupInfo}`;
    }
    return `Go to slides ${slide}-${lastSlide} of ${totalSlides} ${groupInfo}`;
  },
  liveRegionContent: (hasAnyGroupedSlides, firstSlideIndex, lastSlideIndex, totalSlides, selectedSnapIndex, totalSnaps) => {
    const slide = firstSlideIndex + 1;
    const lastSlide = lastSlideIndex + 1;
    const isSlideGrouped = firstSlideIndex !== lastSlideIndex;
    const groupInfo = `(group ${selectedSnapIndex + 1} of ${totalSnaps})`;
    if (!hasAnyGroupedSlides) {
      return `Showing slide ${slide} of ${totalSlides}`;
    }
    if (!isSlideGrouped) {
      return `Showing slide ${slide} of ${totalSlides} ${groupInfo}`;
    }
    return `Showing slides ${slide}-${lastSlide} of ${totalSlides} ${groupInfo}`;
  }
};

function AttributeHandler(initialNode) {
  const attributeStore = new Map();
  let node = initialNode || null;
  function connect(element) {
    node = element;
  }
  function set(key, value) {
    if (!node) return;
    if (!value) return;
    attributeStore.set(key, value);
    node.setAttribute(key, value);
  }
  function toggle(key, value) {
    if (!node) return;
    if (!value) return remove(key);
    set(key, value.toString());
  }
  function remove(key) {
    if (!node) return;
    attributeStore.delete(key);
    node.removeAttribute(key);
  }
  function removeAll() {
    if (!node) return;
    attributeStore.forEach((_, key) => remove(key));
    attributeStore.clear();
  }
  const self = {
    connect,
    set,
    toggle,
    remove,
    removeAll
  };
  return self;
}

function isString(value) {
  return typeof value === 'string';
}
function getChildNode(root, subject) {
  const node = isString(subject) ? root.querySelector(subject) : subject;
  return node;
}
function getChildNodes(root, subject) {
  const node = isString(subject) ? root.querySelectorAll(subject) : subject;
  return node ? Array.from(node) : [];
}
function getAccessibilityRootNode(emblaApi, rootNode) {
  const emblaRootNode = emblaApi.rootNode();
  return rootNode && rootNode(emblaRootNode) || emblaRootNode;
}

function Accessibility(userOptions = {}) {
  const focusableSelectors = ['a[href]', 'area[href]', 'input:not([type="hidden"]):not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', 'button:not([disabled])', 'iframe', 'summary', 'details', 'object', 'embed', 'audio[controls]', 'video[controls]', '[tabindex]:not([tabindex="-1"])', '[contenteditable]:not([contenteditable="false"])'];
  const focusSelector = focusableSelectors.map(selector => `${selector}:not([hidden])`).join(',');
  let options;
  let emblaApi;
  let isSsr = false;
  let destroyed = false;
  let rootNode;
  let liveRegionNode = null;
  let dotsObserver = null;
  let allSnaps = [];
  let isUpdatingLiveRegion = false;
  let lastFocusedSlide = null;
  let focusNodesBySlide = [];
  let rootAttributes;
  let prevButtonAttributes;
  let nextButtonAttributes;
  let liveRegionAttributes;
  let dotsAttributes = [];
  let slidesAttributes = [];
  function pluginIsActive() {
    if (isSsr) return false;
    if (destroyed) return false;
    return options.active;
  }
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;
    const {
      mergeOptions,
      optionsAtMedia
    } = optionsHandler;
    const optionsBase = mergeOptions(defaultOptions, Accessibility.globalOptions);
    const allOptions = mergeOptions(optionsBase, userOptions);
    destroyed = false;
    options = optionsAtMedia(allOptions);
    isSsr = emblaApi.internalEngine().isSsr;
    if (!pluginIsActive()) return;
    rootAttributes = AttributeHandler();
    prevButtonAttributes = AttributeHandler();
    nextButtonAttributes = AttributeHandler();
    liveRegionAttributes = AttributeHandler();
    dotsAttributes = emblaApi.snapList().map(() => AttributeHandler());
    slidesAttributes = emblaApi.slideNodes().map(() => AttributeHandler());
    rootNode = getAccessibilityRootNode(emblaApi, options.rootNode);
    allSnaps = emblaApi.snapList().map((_, index) => index);
    focusNodesBySlide = emblaApi.slideNodes().map(() => []);
    isUpdatingLiveRegion = options.announceChanges;
    setupCarousel();
    setupSlides();
    emblaApi.on('select', onSelect);
  }
  function destroy() {
    if (!pluginIsActive()) return;
    emblaApi.off('select', onSelect);
    rootAttributes.removeAll();
    prevButtonAttributes.removeAll();
    nextButtonAttributes.removeAll();
    liveRegionAttributes.removeAll();
    slidesAttributes.forEach(slideAttributes => slideAttributes.removeAll());
    dotsAttributes.forEach(dotAttributes => dotAttributes.removeAll());
    focusNodesBySlide.flat().forEach(slideFocusNode => {
      const {
        focusAttributes,
        prevTabIndex
      } = slideFocusNode;
      focusAttributes.toggle('tabindex', prevTabIndex);
    });
  }
  function setupCarousel() {
    if (!pluginIsActive()) return;
    const {
      carouselRole,
      carouselAriaLabel,
      carouselAriaRoleDescription
    } = options;
    if (dotsObserver) dotsObserver.disconnect();
    rootAttributes.connect(emblaApi.rootNode());
    rootAttributes.set('role', carouselRole);
    rootAttributes.set('aria-label', carouselAriaLabel);
    rootAttributes.set('aria-roledescription', carouselAriaRoleDescription);
  }
  function onSlideFocusIn(slideIndex) {
    return () => {
      lastFocusedSlide = slideIndex;
      const {
        snapBySlide
      } = emblaApi.internalEngine().scrollSnapList;
      const snapIndex = snapBySlide[lastFocusedSlide];
      updateSlides([snapIndex]);
    };
  }
  function onSlideFocusOut() {
    if (lastFocusedSlide === null || lastFocusedSlide < 0) return;
    const {
      snapBySlide
    } = emblaApi.internalEngine().scrollSnapList;
    const snapIndex = snapBySlide[lastFocusedSlide];
    updateSlides([snapIndex]);
    lastFocusedSlide = null;
  }
  function setupSlides() {
    if (!pluginIsActive()) return;
    const {
      scrollSnapList,
      eventStore
    } = emblaApi.internalEngine();
    const {
      slideRole,
      slideAriaRoleDescription,
      slideAriaLabel
    } = options;
    const focusOptions = {
      capture: true
    };
    emblaApi.slideNodes().forEach((slideNode, slideIndex) => {
      const snapIndex = scrollSnapList.snapBySlide[slideIndex];
      const label = getAriaText(snapIndex, slideAriaLabel);
      const slideAttributes = slidesAttributes[slideIndex];
      const slideFocusNodes = getChildNodes(slideNode, focusSelector);
      slideAttributes.connect(slideNode);
      slideAttributes.set('role', slideRole);
      slideAttributes.set('aria-roledescription', slideAriaRoleDescription);
      slideAttributes.set('aria-label', label);
      eventStore.add(slideNode, 'focusout', onSlideFocusOut, focusOptions);
      eventStore.add(slideNode, 'focusin', onSlideFocusIn(slideIndex), focusOptions);
      focusNodesBySlide[slideIndex] = slideFocusNodes.map(focusNode => ({
        focusAttributes: AttributeHandler(focusNode),
        prevTabIndex: focusNode.getAttribute('tabindex')
      }));
    });
    updateSlides();
  }
  function setupDotButtons(dotsWrapper) {
    if (!pluginIsActive()) return;
    const {
      dotButtonAriaLabel
    } = options;
    const wrapper = getChildNode(rootNode, dotsWrapper);
    const dotNodes = getChildNodes(wrapper, wrapper === null || wrapper === void 0 ? void 0 : wrapper.children);
    dotsAttributes = dotNodes.map(() => AttributeHandler());
    dotNodes.forEach((dotNode, snapIndex) => {
      const label = getAriaText(snapIndex, dotButtonAriaLabel);
      const dotAttributes = dotsAttributes[snapIndex];
      dotAttributes.connect(dotNode);
      dotAttributes.set('aria-label', label);
    });
    if (wrapper) {
      if (dotsObserver) dotsObserver.disconnect();
      dotsObserver = new MutationObserver(() => setupDotButtons(dotsWrapper));
      dotsObserver.observe(wrapper, {
        childList: true
      });
    }
    updateDotButtons();
  }
  function setupPrevAndNextButtons(prevButton, nextButton) {
    if (!pluginIsActive()) return;
    const {
      previousButtonAriaLabel,
      nextButtonAriaLabel
    } = options;
    prevButtonAttributes.connect(getChildNode(rootNode, prevButton));
    prevButtonAttributes.set('aria-label', previousButtonAriaLabel);
    nextButtonAttributes.connect(getChildNode(rootNode, nextButton));
    nextButtonAttributes.set('aria-label', nextButtonAriaLabel);
    updatePrevAndNextButtons();
  }
  function setupLiveRegion(liveRegion) {
    if (!pluginIsActive()) return;
    liveRegionNode = getChildNode(rootNode, liveRegion);
    liveRegionAttributes.connect(liveRegionNode);
    liveRegionAttributes.set('aria-live', 'polite');
    liveRegionAttributes.set('aria-atomic', 'true');
    updateLiveRegion();
  }
  function onSelect(emblaApi) {
    const previousSnap = emblaApi.previousSnap();
    const selectedSnap = emblaApi.selectedSnap();
    const snapsToUpdate = [selectedSnap];
    if (selectedSnap !== previousSnap) snapsToUpdate.push(previousSnap);
    updateSlides(snapsToUpdate);
    updateDotButtons(snapsToUpdate);
    updatePrevAndNextButtons();
    updateLiveRegion();
  }
  function setUpdateLiveRegion(enable) {
    isUpdatingLiveRegion = enable;
    updateLiveRegion();
  }
  function updateLiveRegion() {
    if (!pluginIsActive()) return;
    if (!liveRegionNode) return;
    if (!isUpdatingLiveRegion) return;
    if (!options.liveRegionContent) return;
    const snapIndex = emblaApi.selectedSnap();
    const label = getAriaText(snapIndex, options.liveRegionContent);
    if (label) liveRegionNode.innerHTML = label;
  }
  function updatePrevAndNextButtons() {
    const isPrevDisabled = !emblaApi.canGoToPrev();
    const isNextDisabled = !emblaApi.canGoToNext();
    prevButtonAttributes.toggle('aria-disabled', isPrevDisabled);
    nextButtonAttributes.toggle('aria-disabled', isNextDisabled);
  }
  function updateDotButtons(snaps = allSnaps) {
    snaps.forEach(snapIndex => {
      const isActive = snapIndex === emblaApi.selectedSnap();
      const dotAttributes = dotsAttributes[snapIndex];
      if (!dotAttributes) return;
      dotAttributes.toggle('aria-current', isActive);
    });
  }
  function updateSlides(snaps = allSnaps) {
    const {
      slidesBySnap
    } = emblaApi.internalEngine().scrollSnapList;
    snaps.forEach(snapIndex => {
      const slidesInSnap = slidesBySnap[snapIndex];
      const isActive = snapIndex === emblaApi.selectedSnap();
      slidesInSnap.forEach(slideIndex => {
        const slideNode = emblaApi.slideNodes()[slideIndex];
        const hasFocusedElement = slideNode.contains(document.activeElement);
        const slideAttributes = slidesAttributes[slideIndex];
        const slideFocusNodes = focusNodesBySlide[slideIndex];
        slideAttributes.toggle('aria-hidden', !isActive && !hasFocusedElement);
        slideFocusNodes.forEach(slideFocusNode => {
          const {
            focusAttributes,
            prevTabIndex
          } = slideFocusNode;
          const tabindex = !isActive ? '-1' : prevTabIndex;
          focusAttributes.toggle('tabindex', tabindex);
        });
      });
    });
  }
  function getAriaText(snapIndex, ariaTextCallback) {
    const {
      slidesBySnap
    } = emblaApi.internalEngine().scrollSnapList;
    const slidesInSnap = slidesBySnap[snapIndex];
    if (!ariaTextCallback) return '';
    if (!allSnaps.length) return '';
    if (!slidesInSnap) return '';
    const hasSlideGroups = slidesBySnap.some(group => group.length > 1);
    const slideCount = emblaApi.slideNodes().length;
    const snapCount = allSnaps.length;
    return ariaTextCallback(hasSlideGroups, slidesInSnap[0], slidesInSnap[slidesInSnap.length - 1], slideCount, snapIndex, snapCount);
  }
  const self = {
    name: 'accessibility',
    options: userOptions,
    init,
    destroy,
    setupPrevAndNextButtons,
    setupDotButtons,
    setupLiveRegion,
    setUpdateLiveRegion,
    updateSlides,
    updateDotButtons,
    updatePrevAndNextButtons,
    updateLiveRegion
  };
  return self;
}
Accessibility.globalOptions = undefined;

// Pre-load a viewport early so touch cards are swipe-ready before they scroll in.
const TOUCH_ROOT_MARGIN = '100% 0px';

/**
 * @param {string} template - Translated string with {{ index }} and {{ total }} placeholders
 * @param {number} slideIndex - Zero-based slide index
 * @param {number} totalSlides - Total slide count
 * @returns {string}
 */
const formatSlideText = (template, slideIndex, totalSlides) =>
  template.replace('{{ index }}', slideIndex + 1).replace('{{ total }}', totalSlides);

class ProductCard extends HTMLElement {
  #anchors = this.querySelectorAll('a');
  #gaItemListName = this.closest('[data-ga-item-list-name]')?.dataset.gaItemListName;
  #productJson = JSON.parse(this.querySelector('[data-product-json]').textContent);

  // Only present on multi-image cards.
  #viewport = this.querySelector('[data-carousel-viewport]');
  #template = this.querySelector('[data-carousel-slides]');
  #prevButton = this.querySelector('[data-carousel-prev]');
  #nextButton = this.querySelector('[data-carousel-next]');
  #progressThumb = this.querySelector('[data-carousel-progress] .embla__scrollbar__thumb');
  #liveRegion = this.querySelector('[data-carousel-live]');

  // Only present inside the collection/search grid, where the layout can switch.
  #grid = this.closest('product-grid');

  #hoverMql = window.matchMedia('(hover: hover)');

  /** @type {import('embla-carousel').EmblaCarouselType} */
  #embla;
  #isLive = false;
  #unobserve;
  #promotionController;
  #liveController;
  #unsubscribeLayout;

  connectedCallback() {
    this.#bindAnchors(this.#anchors);
    this.sendAnalytics();
    this.#initCarouselTriggers();
  }

  disconnectedCallback() {
    this.#promotionController?.abort();
    this.#liveController?.abort();
    this.#unsubscribeLayout?.();
    this.#unobserve?.();
    this.#embla?.destroy();
  }

  /**
   * @param {NodeListOf<HTMLAnchorElement>} anchors
   */
  #bindAnchors(anchors) {
    anchors.forEach((anchor) => anchor.addEventListener('click', () => this.onAnchorClick()));
  }

  /**
   * Set the item list name in session storage when the anchor is clicked
   * This is used to track the item list name for GA4 events
   */
  onAnchorClick() {
    sessionStorage.setItem(
      'gaProductJson',
      JSON.stringify({
        ...this.#productJson,
        itemListName: this.#gaItemListName,
      })
    );
  }

  sendAnalytics() {
    Shopify.analytics.publish('karl:product_variants', {
      productVariants: [this.#productJson],
    });
  }

  /**
   * Wait for interaction before building the carousel so a full grid of cards
   * doesn't create an Embla instance each on load.
   */
  #initCarouselTriggers() {
    if (!this.#viewport) return;

    if (this.#grid) {
      this.#unsubscribeLayout = Pubsub.subscribe('collection:layoutChanged', this.#onLayoutChanged);
    }

    this.#promotionController = new AbortController();
    const { signal } = this.#promotionController;

    this.addEventListener('focusin', this.#promote, { signal });
    this.addEventListener('touchstart', this.#promote, { passive: true, signal });

    if (this.#hoverMql.matches) {
      this.addEventListener('pointerenter', this.#promote, { signal });
      return;
    }

    this.#unobserve = onIntersect(this, this.#promote, { rootMargin: TOUCH_ROOT_MARGIN });
  }

  #promote = () => {
    if (this.#isLive) return;
    this.#isLive = true;

    this.#promotionController?.abort();
    this.#unobserve?.();

    this.#appendTemplateSlides();
    this.#createCarousel();
  };

  /**
   * Move the remaining slides out of the template and into the container. Their
   * images aren't requested until this runs.
   */
  #appendTemplateSlides() {
    const container = this.#viewport.querySelector('.embla__container');
    if (!container || !this.#template) return;

    const slides = this.#template.content.cloneNode(true);

    // Match the clones to the layout the grid switcher may have set on the first image.
    const referenceSizes = this.#viewport.querySelector('img')?.sizes;
    if (referenceSizes)
      slides.querySelectorAll('img').forEach((image) => (image.sizes = referenceSizes));

    // Bind before appending, so the first slide's already-bound anchor isn't bound twice.
    this.#bindAnchors(slides.querySelectorAll('a'));
    container.append(slides);
  }

  #createCarousel() {
    this.#embla = EmblaCarousel(
      this.#viewport,
      { loop: true, skipSnaps: true, slideChanges: false, draggable: !this.#isCompactLayout() },
      [Accessibility(this.#accessibilityOptions())]
    );

    this.#embla.plugins().accessibility?.setupLiveRegion(this.#liveRegion);

    this.#wireControls();
    this.#containParentGesture();
  }

  /**
   * Translated labels for the accessibility plugin, and a 'group' role instead
   * of its 'region' default so a grid of promoted cards doesn't fill the
   * landmark list with identical "Carousel" entries.
   */
  #accessibilityOptions() {
    const translations = window.a11y_translations.product_card;

    return {
      announceChanges: true,
      carouselRole: 'group',
      carouselAriaLabel: translations.carousel_label,
      slideAriaLabel: (grouped, firstIndex, lastIndex, totalSlides) =>
        formatSlideText(translations.slide_label, firstIndex, totalSlides),
      liveRegionContent: (grouped, firstIndex, lastIndex, totalSlides) =>
        formatSlideText(translations.slide_announcement, firstIndex, totalSlides),
    };
  }

  #wireControls() {
    if (this.#prevButton && this.#nextButton)
      emblaNavigation(this.#embla, this.#prevButton, this.#nextButton);

    if (this.#progressThumb) emblaScrollbar(this.#embla, this.#progressThumb);
  }

  /**
   * Keep a card swipe from dragging a parent carousel (featured products, shop
   * the look). Scoped to the viewport so gestures on title, price and swatches
   * still reach the parent.
   */
  #containParentGesture() {
    this.#liveController = new AbortController();
    const { signal } = this.#liveController;
    this.#viewport.addEventListener('mousedown', this.#containGesture, { signal });
    this.#viewport.addEventListener('touchstart', this.#containGesture, { passive: true, signal });
  }

  /** @param {Event} event */
  #containGesture = (event) => {
    event.stopPropagation();
  };

  /**
   * Compact is an image-only layout: its carousel UI is hidden, so dragging
   * goes off with it.
   */
  #isCompactLayout() {
    return this.#grid?.dataset.layout === 'compact';
  }

  /** @param {{ layout: string }} options */
  #onLayoutChanged = ({ layout }) => {
    if (!this.#embla) return;

    if (layout === 'compact') {
      // Back to the featured image, since compact shows the image alone.
      this.#embla.reInit({ draggable: false, startSnap: 0 });
    } else {
      this.#embla.reInit({ draggable: true });
    }

    // The accessibility plugin drops its live region connection on reinit.
    this.#embla.plugins().accessibility?.setupLiveRegion(this.#liveRegion);
  };
}

customElements.define('product-card', ProductCard);
